import type { Person, ResyncEntry } from '../../../supabase/functions/_shared/core/index.ts';
import { busy, field, fmtDate, h, mount, toast } from '../dom.ts';
import { uuid } from '../api.ts';
import { reload, type Store } from '../state.ts';
import { openModal } from './modal.ts';
import { exportPeople, openImportModal } from './people-import.ts';

import { publicBaseUrl } from '../config.ts';

// En démo (page unique sans réécriture d'URL), le lien passe par le hash.
const installLink = (api: { demo: boolean }, p: Person) =>
  api.demo ? `${location.href.split('#')[0]}#/install/${p.install_token}` : `${publicBaseUrl}/install/${p.install_token}`;

export function peopleView(store: Store, refresh: () => void): HTMLElement {
  const root = h('div', { class: 'panel' });
  const search = h('input', { type: 'search', placeholder: 'Rechercher…' });

  const edit = (p: Person | null) => {
    const isNew = !p;
    const cur: Person = p ?? {
      id: uuid(),
      primary_email: '',
      kind: 'person',
      first_name: null,
      last_name: null,
      display_name: null,
      job_title: null,
      phone_mobile: null,
      phone_office: null,
      brand_id: null,
      active: true,
      managed: true,
      no_banner: false,
      send_as_emails: [],
      local_last_seen_at: null,
      install_token: uuid().replace(/-/g, ''),
      public_token: uuid().replace(/-/g, ''),
    };
    const txt = (v: string | null, ph = '') => h('input', { value: v ?? '', placeholder: ph });
    const email = h('input', {
      type: 'email',
      value: cur.primary_email,
      disabled: !isNew,
    });
    const kind = h(
      'select',
      null,
      h('option', { value: 'person' }, 'Personne'),
      h('option', { value: 'shared' }, 'Boîte partagée (contact@, booking@…)'),
    );
    kind.value = cur.kind;
    const first = txt(cur.first_name),
      last = txt(cur.last_name),
      display = txt(cur.display_name, 'optionnel');
    // Plusieurs fonctions = une par ligne (rendues avec un <br> dans le bloc Fonction). Pas de colonne « poste 2 ».
    const title = h('textarea', { rows: 2, placeholder: 'ex. Responsable booking\nUne fonction par ligne si plusieurs', style: 'resize:vertical' }, cur.job_title ?? '') as unknown as HTMLInputElement;
    const mobile = txt(cur.phone_mobile, '06 …'),
      office = txt(cur.phone_office, '01 …');
    const brand = h(
      'select',
      null,
      h('option', { value: '' }, 'Automatique (selon le domaine)'),
      store.brands.map((b) => h('option', { value: b.id }, b.name)),
    );
    brand.value = cur.brand_id ?? '';
    const active = h('input', { type: 'checkbox', checked: cur.active });
    const managed = h('input', { type: 'checkbox', checked: cur.managed });
    const noBanner = h('input', { type: 'checkbox', checked: cur.no_banner });
    const localClient = h('input', { type: 'checkbox', checked: cur.local_client === true });
    const save = h('button', null, 'Enregistrer');
    const rotate = isNew ? null : h('button', {
      class: 'ghost',
      type: 'button',
      title: 'L’ancien lien cessera de fonctionner',
      onclick: () => {
        cur.install_token = uuid().replace(/-/g, '');
        rotate!.textContent = 'Nouveau lien prêt — enregistrer';
      },
    }, 'Régénérer le lien d’installation');
    const close = openModal(
      isNew ? 'Ajouter une boîte' : cur.primary_email,
      h(
        'div',
        { class: 'grid2' },
        field(
          'Adresse principale',
          email,
          isNew ? 'Doit exister dans Google Workspace' : 'Gérée par Google',
        ),
        field('Type', kind),
        field('Prénom', first),
        field('Nom', last),
        field(
          'Nom affiché',
          display,
          'Remplace prénom + nom (utile pour les boîtes partagées)',
        ),
        field('Fonction', title),
        field('Mobile', mobile),
        field('Fixe', office),
        field(
          'Charte (lieu)',
          brand,
          'Force la charte de l’adresse principale',
        ),
        h(
          'div',
          { class: 'checks' },
          h('label', null, active, ' Active'),
          h('label', null, managed, ' Signature gérée par l’outil'),
          h('label', { title: 'Signature standard, mais aucune campagne ni bannière, dans Gmail comme dans Apple Mail / Outlook. Pour les boîtes sensibles (signalement, RH, direction…). Ce réglage n’est jamais écrasé par la synchronisation Google ni par un import.' }, noBanner, ' Jamais de bannière — boîte sensible (signalement, RH…) : signature standard, aucune campagne'),
          h('label', { title: 'Cible de « Envoyer les liens d’installation » (écran Déploiement)' }, localClient, ' Écrit aussi depuis Apple Mail / Outlook'),
        ),
      ),
      rotate ? [rotate, save] : [save],
    );
    save.addEventListener('click', () =>
      busy(save, async () => {
        const v = (i: HTMLInputElement) => i.value.trim() || null;
        const next: Person = {
          ...cur,
          primary_email: email.value.trim().toLowerCase(),
          kind: kind.value as Person['kind'],
          first_name: v(first),
          last_name: v(last),
          display_name: v(display),
          job_title: title.value.split('\n').map((l) => l.trim()).filter(Boolean).join('\n') || null,
          phone_mobile: v(mobile),
          phone_office: v(office),
          brand_id: brand.value || null,
          active: active.checked,
          managed: managed.checked,
          no_banner: noBanner.checked,
          local_client: localClient.checked,
        };
        if (
          !/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(next.primary_email)
        ) throw new Error('Adresse invalide');
        await store.api.savePerson(next, isNew);
        await reload(store);
        close();
        toast(
          store.api.demo ? 'Enregistré (démo)' : 'Enregistré — appliqué à la prochaine poussée (≤ 15 min)',
        );
        refresh();
      }));
  };

  // « Reprendre depuis Google » : la seule action qui écrase prénom, nom, fonction, téléphone depuis
  // l'annuaire. Toujours un plan champ par champ d'abord, puis une confirmation explicite.
  const resync = async (only: string[] | null, trigger: HTMLButtonElement) => {
    await busy(trigger, async () => {
      const r = await store.api.resync({ dryRun: true, only: only ?? undefined });
      const scope = only ? only.join(', ') : `toutes les personnes (${store.people.length})`;
      if (!r.plan.length) {
        toast(`Rien à reprendre pour ${scope} : les données de l’outil sont déjà celles de Google (ou Google n’a rien de plus).`);
        return;
      }
      const row = (e: ResyncEntry, c: ResyncEntry['changes'][number], i: number) =>
        h('tr', null, i === 0 ? h('td', { rowspan: String(e.changes.length) }, e.email) : null, h('td', null, c.label), h('td', { class: 'muted' }, c.from ?? '— (vide)'), h('td', null, h('strong', null, c.to)));
      const apply = h('button', null, `Écraser ${r.stats.fields} champ${r.stats.fields > 1 ? 's' : ''} depuis Google`);
      const close = openModal(
        'Reprendre depuis Google',
        h(
          'div',
          null,
          h('p', null, `${r.stats.people} personne${r.stats.people > 1 ? 's' : ''}, ${r.stats.fields} champ${r.stats.fields > 1 ? 's' : ''} à écraser. Rien n’est encore écrit : voici, champ par champ, ce qui changerait. Une valeur vide chez Google n’efface jamais la vôtre.`),
          h('div', { class: 'table-wrap' }, h('table', null, h('thead', null, h('tr', null, ['Adresse', 'Champ', 'Dans l’outil', 'Chez Google'].map((t) => h('th', null, t)))), h('tbody', null, r.plan.flatMap((e) => e.changes.map((c, i) => row(e, c, i)))))),
        ),
        [h('button', { class: 'ghost', onclick: () => close() }, 'Annuler'), apply],
      );
      apply.addEventListener('click', () =>
        busy(apply, async () => {
          if (!only) {
            const typed = prompt(`Écrasement de ${r.stats.fields} champ${r.stats.fields > 1 ? 's' : ''} sur ${r.stats.people} personne${r.stats.people > 1 ? 's' : ''} depuis Google. Tapez REPRENDRE pour confirmer.`);
            if (typed !== 'REPRENDRE') return;
          } else if (!confirm(`Écraser ${r.stats.fields} champ${r.stats.fields > 1 ? 's' : ''} de ${only[0]} avec les valeurs Google ?`)) return;
          const done = await store.api.resync({ dryRun: false, only: only ?? undefined });
          close();
          await reload(store);
          refresh();
          toast(done.errors.length ? `${done.applied.length} reprise(s), ${done.errors.length} erreur(s) : ${done.errors.map((e) => `${e.email} (${e.error})`).join(' ; ')}` : `${done.applied.length} personne${done.applied.length > 1 ? 's' : ''} reprise${done.applied.length > 1 ? 's' : ''} depuis Google${store.api.demo ? ' (démo)' : ' — appliqué à la prochaine poussée (≤ 15 min)'}`);
        }));
    });
  };

  const table = h('div', { class: 'table-wrap' });
  const draw = () => {
    const q = search.value.toLowerCase();
    const rows = store.people.filter((p) => !q || JSON.stringify(p).toLowerCase().includes(q));
    const brandName = (id: string | null) => store.brands.find((b) => b.id === id)?.name ?? 'Auto';
    mount(
      table,
      h(
        'table',
        null,
        h(
          'thead',
          null,
          h(
            'tr',
            null,
            [
              'Adresse',
              'Nom',
              'Fonction',
              'Charte',
              'Statut',
              'Logiciel mail',
              '',
            ].map((t) => h('th', null, t)),
          ),
        ),
        h(
          'tbody',
          null,
          rows.map((p) =>
            h(
              'tr',
              { class: p.active ? '' : 'dim' },
              h(
                'td',
                null,
                p.primary_email,
                p.kind === 'shared' ? h('span', { class: 'chip small' }, 'partagée') : null,
              ),
              h(
                'td',
                null,
                p.display_name ||
                  [p.first_name, p.last_name].filter(Boolean).join(' ') || '—',
              ),
              h(
                'td',
                null,
                p.job_title?.replace(/\n/g, ' / ') || h('span', { class: 'warn' }, 'à compléter'),
              ),
              h('td', null, brandName(p.brand_id)),
              h(
                'td',
                null,
                !p.active ? 'Inactive' : p.managed ? 'Gérée' : 'Non gérée',
                p.no_banner ? h('span', { class: 'chip small nobanner', title: 'Jamais de bannière : boîte sensible' }, 'Sans bannière') : null,
              ),
              h(
                'td',
                {
                  title: 'Dernier affichage d’une signature installée dans Apple Mail ou Outlook',
                },
                p.local_client ? h('span', { class: 'chip small' }, 'Apple Mail / Outlook') : null,
                p.local_last_seen_at
                  ? h(
                    'span',
                    { class: 'chip live' },
                    fmtDate(p.local_last_seen_at),
                  )
                  : p.local_client ? h('span', { class: 'muted' }, ' pas encore installée') : h('span', { class: 'muted' }, '—'),
              ),
              h(
                'td',
                { class: 'actions' },
                h('button', {
                  class: 'ghost',
                  onclick: () => edit(p),
                }, 'Modifier'),
                p.google_id
                  ? h('button', {
                    class: 'ghost',
                    title: 'Écraser prénom, nom, fonction et téléphone avec les valeurs de l’annuaire Google — après un aperçu champ par champ',
                    onclick: (e: Event) => resync([p.primary_email], e.currentTarget as HTMLButtonElement),
                  }, 'Reprendre depuis Google')
                  : null,
                h('button', {
                  class: 'ghost',
                  title: 'Lien à envoyer aux utilisateurs Apple Mail / Outlook',
                  onclick: async () => {
                    await navigator.clipboard.writeText(
                      installLink(store.api, p),
                    );
                    toast('Lien d’installation copié');
                  },
                }, 'Lien install.'),
              ),
            )
          ),
        ),
      ),
    );
  };
  search.addEventListener('input', draw);
  mount(
    root,
    h(
      'div',
      { class: 'toolbar' },
      h('h2', null, `Personnes et boîtes (${store.people.length})`),
      search,
      h(
        'button',
        { class: 'ghost', onclick: () => exportPeople(store) },
        'Exporter (Excel)',
      ),
      h('button', {
        class: 'ghost',
        onclick: () => openImportModal(store, refresh),
      }, 'Importer un fichier'),
      h('button', {
        class: 'ghost',
        title: 'Pour tout le monde : aperçu champ par champ, puis confirmation',
        onclick: (e: Event) => resync(null, e.currentTarget as HTMLButtonElement),
      }, 'Reprendre depuis Google (tous)'),
      h('button', { onclick: () => edit(null) }, '+ Ajouter'),
    ),
    h(
      'p',
      { class: 'muted' },
      'Les comptes viennent de l’annuaire Google (onglet Déploiement). La synchronisation n’ajoute que les nouvelles adresses : elle n’écrase jamais prénom, nom, fonction ni téléphone. Pour tirer une correction faite dans Google, « Reprendre depuis Google », par personne ou pour tous, après un aperçu champ par champ. ' +
        'Tout le monde reçoit la signature Gmail ; le lien d’installation s’ajoute pour ceux qui écrivent aussi depuis Apple Mail ou Outlook. ' +
        'La colonne « Logiciel mail » se remplit toute seule dès qu’une signature installée s’affiche.',
    ),
    table,
  );
  draw();
  return root;
}
